import { Flex, Heading, Icon, Skeleton, Text, VStack } from '@chakra-ui/react';
import { PencilIcon, PlusIcon } from 'lucide-react';
import { getPartyFinancingTypeLabel } from '~/apps/consumer/utils/party.utils';
import { GradientBorder } from '~/common/components/GradientBorder/GradientBorder';
import { GradientIcon } from '~/common/components/GradientIcon';
import { formatPercent } from '~/common/utils/formatter';
import { formatToCurrency } from '~/services/document/utils/number';
import { UserAvatar } from '../../User/UserAvatar';
import { useBuyerOfferAnalysisContext } from '../Context/BuyerOfferAnalysisContext';

export const BuyerInfo = () => {
  const {
    buyerOfferAnalysis,
    buyerOfferAnalysisLoading,
    onEditPartyModalOpen,
    isViewOnly,
  } = useBuyerOfferAnalysisContext();

  const party = buyerOfferAnalysis?.party;
  const buyer = party?.primaryConsumer;

  const financingType = getPartyFinancingTypeLabel(party);
  const downPayment = formatPercent(
    party?.partyFinancing?.percentDownPayment,
    3
  );
  const cashReserves = formatToCurrency(party?.partyFinancing?.cashReserves, 0);

  let info = (
    <Flex
      data-testid="buyer-info"
      direction="column"
      gap={4}
      height="250px"
      p={5}
      width="100%"
    >
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Heading color="whiteAlpha.600" fontSize="sm" size="smallUppercase">
          Buyer
        </Heading>
        {!isViewOnly && (
          <Icon
            as={PencilIcon}
            color="whiteAlpha.600"
            cursor="pointer"
            onClick={onEditPartyModalOpen}
          />
        )}
      </Flex>
      <VStack alignItems="center">
        <UserAvatar
          size="lg"
          user={{
            fullName: buyer?.isEntity ? buyer?.entityName : buyer?.fullName,
            profilePicture: buyer?.profilePicture,
          }}
        />
        <VStack spacing={0}>
          <Text fontWeight="medium" size="lg">
            {buyer?.isEntity ? buyer?.entityName : buyer?.fullName}
          </Text>
          {financingType && <Text color="whiteAlpha.700">{financingType}</Text>}
          {downPayment && (
            <Text color="whiteAlpha.700">{downPayment} down</Text>
          )}
          {cashReserves && (
            <Text color="whiteAlpha.700">{cashReserves} reserves</Text>
          )}
        </VStack>
      </VStack>
    </Flex>
  );

  if (buyerOfferAnalysisLoading) {
    info = <Skeleton>{info}</Skeleton>;
  }

  if (!party || !buyer) {
    info = (
      <Flex height="250px" p={5} width="100%">
        <GradientBorder
          alignItems="center"
          cursor="pointer"
          gradient="blueGradient"
          justifyContent="center"
          width="100%"
          onClick={onEditPartyModalOpen}
        >
          <Flex
            alignItems="center"
            direction="column"
            gap={1}
            justifyContent="center"
          >
            <GradientIcon
              boxSize="32px"
              gradientThemeKey="blue"
              icon={PlusIcon}
            />
            <Text bg="blueGradient" bgClip="text" fontWeight="medium">
              Add a buyer
            </Text>
          </Flex>
        </GradientBorder>
      </Flex>
    );
  }

  return info;
};
